import React, { useCallback, useEffect, useState } from 'react';

import { CheckCircle, RadioButtonUnchecked, Schedule } from '@mui/icons-material';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Typography,
} from '@mui/material';

import { useApiClient, useToastService } from '@@common';

import { AddEnvironmentComponentRequest, ComponentModel, EnvironmentClient, EnvironmentModel, ReleasePlanModel } from '../../../api/api';
import ReleasePlanProgressText from './ReleasePlanProgressText';

// Base props for all dialog operations
interface BaseDialogProps {
	open: boolean;
	onClose: () => void;
	environment: EnvironmentModel; // The target environment that will be affected
	onSuccess?: (releasePlanId?: string, targetEnvironment?: EnvironmentModel) => void;
}

// Props for environment promotion
interface PromotionDialogProps extends BaseDialogProps {
	operation: 'promote';
	sourceEnvironment: EnvironmentModel;
}

// Props for component operations
interface ComponentDialogProps extends BaseDialogProps {
	operation: 'addComponent' | 'updateComponent' | 'removeComponent' | 'redeployComponent' | 'deleteComponent';
	component: ComponentModel;
	currentVersion?: string; // For update operations, shows current deployed version
}

type PromoteDialogProps = PromotionDialogProps | ComponentDialogProps;

export default function PromoteDialog(props: PromoteDialogProps) {
	const { open, onClose, onSuccess } = props;
	const [isExecuting, setIsExecuting] = useState(false);
	const [releasePlan, setReleasePlan] = useState<ReleasePlanModel | null>(null);
	const [isLoadingReleasePlan, setIsLoadingReleasePlan] = useState(false);

	const client = useApiClient(EnvironmentClient);
	const toastService = useToastService();

	const handleExecute = useCallback(async () => {
		setIsExecuting(true);
		try {
			let result: { releasePlanId: string } | null = null;
			let successMessage = '';
			let targetEnv: EnvironmentModel | undefined;

			if (props.operation === 'promote') {
				// Environment promotion
				result = await client.promote(props.sourceEnvironment.id, props.environment.id);
				successMessage = `Promotion initiated from "${props.sourceEnvironment.name}" to "${props.environment.name}"`;
				targetEnv = props.environment;
			} else if (props.operation === 'addComponent' || props.operation === 'updateComponent' || props.operation === 'redeployComponent') {
				// Component add/update/redeploy
				const request: AddEnvironmentComponentRequest = {
					componentId: props.component.id,
					containerImageVersion:
						props.operation === 'redeployComponent' ? props.currentVersion || props.component.containerImageVersion : props.component.containerImageVersion,
				};
				result = await client.addEnvironmentComponent(props.environment.id, request);
				const actionText = props.operation === 'addComponent' ? 'added to' : props.operation === 'updateComponent' ? 'updated in' : 'redeployed in';
				successMessage = `Component "${props.component.name}" ${actionText} environment "${props.environment.name}"`;
				targetEnv = props.environment;
			} else if (props.operation === 'removeComponent') {
				// Component removal from environment
				result = await client.removeEnvironmentComponent(props.environment.id, props.component.id);
				successMessage = `Component "${props.component.name}" removed from environment "${props.environment.name}"`;
				targetEnv = props.environment;
			} else if (props.operation === 'deleteComponent') {
				// Component deletion (complete removal from system)
				await client.removeComponent(props.component.id);
				successMessage = `Component "${props.component.name}" deleted successfully`;
				targetEnv = props.environment;
			}

			toastService.success(successMessage);

			// Load the release plan using the returned releasePlanId
			if (result?.releasePlanId) {
				setIsLoadingReleasePlan(true);
				try {
					const plan = await client.getReleasePlanById(result.releasePlanId);
					setReleasePlan(plan);
				} catch (error) {
					console.error('Error loading release plan:', error);
					toastService.error('Failed to load release plan details');
				} finally {
					setIsLoadingReleasePlan(false);
				}
			}

			onSuccess?.(result?.releasePlanId, targetEnv);
		} catch (error) {
			console.error('Error executing operation:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to execute operation: ${errorMessage}`);
		} finally {
			setIsExecuting(false);
		}
	}, [props, client, toastService, onSuccess]);

	const handleClose = useCallback(() => {
		if (!isExecuting && !isLoadingReleasePlan) {
			setReleasePlan(null);
			onClose();
		}
	}, [isExecuting, isLoadingReleasePlan, onClose]);

	// Reset state when dialog opens
	useEffect(() => {
		if (open) {
			setReleasePlan(null);
			setIsLoadingReleasePlan(false);
			setIsExecuting(false);
		}
	}, [open]);

	const getStepIcon = (stepIndex: number) => {
		if (!releasePlan || releasePlan.completedStepCount === undefined) {
			return <RadioButtonUnchecked color="disabled" />;
		}

		if (stepIndex < releasePlan.completedStepCount) {
			return <CheckCircle color="success" />;
		} else if (stepIndex === releasePlan.completedStepCount) {
			return <Schedule color="primary" />;
		} else {
			return <RadioButtonUnchecked color="disabled" />;
		}
	};

	const getOperationDescription = () => {
		if (props.operation === 'promote') {
			return {
				title: 'Promote Components',
				description: `Promote components from ${props.sourceEnvironment.name} to ${props.environment.name}?`,
				detail: 'This will initiate the deployment process to move components to the next environment.',
				progressTitle: `Promoting from ${props.sourceEnvironment.name} to ${props.environment.name}`,
			};
		} else if (props.operation === 'addComponent') {
			return {
				title: 'Add Component',
				description: `Add "${props.component.name}" (v${props.component.containerImageVersion}) to environment "${props.environment.name}"?`,
				detail: 'This will deploy the component to the selected environment.',
				progressTitle: `Adding ${props.component.name} to ${props.environment.name}`,
			};
		} else if (props.operation === 'updateComponent') {
			return {
				title: 'Update Component',
				description: `Update "${props.component.name}" in environment "${props.environment.name}"?`,
				detail: `This will update the component from version ${props.currentVersion} to ${props.component.containerImageVersion}.`,
				progressTitle: `Updating ${props.component.name} in ${props.environment.name}`,
			};
		} else if (props.operation === 'redeployComponent') {
			return {
				title: 'Redeploy Component',
				description: `Redeploy "${props.component.name}" (v${props.currentVersion || props.component.containerImageVersion}) in environment "${
					props.environment.name
				}"?`,
				detail: `This will redeploy the component with the same version that is currently deployed.`,
				progressTitle: `Redeploying ${props.component.name} in ${props.environment.name}`,
			};
		} else if (props.operation === 'removeComponent') {
			return {
				title: 'Remove Component',
				description: `Remove "${props.component.name}" from environment "${props.environment.name}"?`,
				detail: 'This will remove the component from the selected environment.',
				progressTitle: `Removing ${props.component.name} from ${props.environment.name}`,
			};
		} else if (props.operation === 'deleteComponent') {
			return {
				title: 'Delete Component',
				description: `Delete component "${props.component.name}" (v${props.component.containerImageVersion})?`,
				detail: 'This will permanently delete the component from the system. This action cannot be undone.',
				progressTitle: `Deleting ${props.component.name}`,
			};
		}
		return {
			title: 'Operation',
			description: 'Loading...',
			detail: '',
			progressTitle: 'Processing...',
		};
	};

	const operationInfo = getOperationDescription();

	const renderContent = () => {
		if (releasePlan) {
			return (
				<Box sx={{ py: 2 }}>
					<Typography variant="body1" gutterBottom>
						Release Plan Progress
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						{operationInfo.progressTitle}
					</Typography>

					{isLoadingReleasePlan ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
							<CircularProgress size={24} />
						</Box>
					) : (
						<List dense>
							{releasePlan.steps?.map((step, index) => (
								<ListItem key={index}>
									<ListItemIcon>{getStepIcon(index)}</ListItemIcon>
									<ListItemText
										primary={<ReleasePlanProgressText text={step.displayName || ''} />}
										secondary={
											releasePlan.completedStepCount !== undefined && index < releasePlan.completedStepCount
												? 'Completed'
												: releasePlan.completedStepCount === index
													? 'In Progress'
													: 'Pending'
										}
									/>
								</ListItem>
							)) || <Typography variant="body2">No steps available</Typography>}
						</List>
					)}
				</Box>
			);
		}

		return (
			<Box sx={{ py: 2 }}>
				<Typography variant="body1" gutterBottom>
					{operationInfo.description}
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
					{operationInfo.detail}
				</Typography>
			</Box>
		);
	};

	const getActionButtonText = () => {
		if (isExecuting) {
			switch (props.operation) {
				case 'promote':
					return 'Promoting...';
				case 'addComponent':
					return 'Adding...';
				case 'updateComponent':
					return 'Updating...';
				case 'redeployComponent':
					return 'Redeploying...';
				case 'removeComponent':
					return 'Removing...';
				case 'deleteComponent':
					return 'Deleting...';
				default:
					return 'Processing...';
			}
		}

		switch (props.operation) {
			case 'promote':
				return 'Promote';
			case 'addComponent':
				return 'Add Component';
			case 'updateComponent':
				return 'Update Component';
			case 'redeployComponent':
				return 'Redeploy Component';
			case 'removeComponent':
				return 'Remove Component';
			case 'deleteComponent':
				return 'Delete Component';
			default:
				return 'Execute';
		}
	};

	const getButtonColor = () => {
		return props.operation === 'removeComponent' || props.operation === 'deleteComponent' ? 'error' : 'primary';
	};

	const renderActions = () => {
		const isLoading = isExecuting || isLoadingReleasePlan;

		if (releasePlan) {
			return (
				<Button onClick={handleClose} disabled={isLoading}>
					Close
				</Button>
			);
		}

		return (
			<>
				<Button onClick={handleClose} disabled={isLoading}>
					Cancel
				</Button>
				<Button
					onClick={handleExecute}
					variant="contained"
					color={getButtonColor()}
					disabled={isLoading}
					startIcon={isExecuting ? <CircularProgress size={16} /> : undefined}
				>
					{getActionButtonText()}
				</Button>
			</>
		);
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>{operationInfo.title}</DialogTitle>
			<DialogContent>{renderContent()}</DialogContent>
			<DialogActions>{renderActions()}</DialogActions>
		</Dialog>
	);
}
